import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { songsApi } from '@/services/api/songs';
import { usePlaylists } from '@/context/PlaylistContext';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import CoverArt from '@/components/common/CoverArt';
import { ArrowLeft, Check } from 'lucide-react';
import { Song, SongRevision } from '@/types/song';

interface TrackSelection {
  songId: number;
  songName: string;
  coverArtUrl?: string;
  revisionId?: number;
  revisionName?: string;
}

export default function AddTracksPage() {
  const { playlistId } = useParams<{ playlistId: string }>();
  const navigate = useNavigate();
  const { addTracksToPlaylist } = usePlaylists();
  const [selectedTracks, setSelectedTracks] = useState<Set<string>>(new Set());
  const [songDetails, setSongDetails] = useState<Map<number, { song: Song; revisions: SongRevision[] }>>(new Map());

  const { data: songsData, isLoading } = useQuery({
    queryKey: ['songs'],
    queryFn: () => songsApi.listSongs({ limit: 1000 }),
  });

  const songs = songsData?.songs || [];

  // Load song details for songs with revisions
  useEffect(() => {
    async function loadSongDetails() {
      const songsWithRevisions = songs.filter(song => song.revisionCount && song.revisionCount > 0);

      for (const song of songsWithRevisions) {
        if (!songDetails.has(song.songId)) {
          try {
            const details = await songsApi.getSongDetails(song.songId, { includeRevisions: true, includeComments: false });
            setSongDetails(prev => new Map(prev).set(song.songId, { song: details.song, revisions: details.revisions }));
          } catch (error) {
            console.error(`Failed to load details for song ${song.songId}:`, error);
          }
        }
      }
    }

    if (songs.length > 0) {
      loadSongDetails();
    }
  }, [songs]);

  function getTrackKey(songId: number, revisionId?: number): string {
    return revisionId ? `${songId}-${revisionId}` : `${songId}`;
  }

  function toggleTrack(songId: number, songName: string, coverArtUrl?: string, revisionId?: number, revisionName?: string) {
    const key = getTrackKey(songId, revisionId);
    const newSelected = new Set(selectedTracks);

    console.log(songName, coverArtUrl, revisionName);

    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }

    setSelectedTracks(newSelected);
  }

  function isTrackSelected(songId: number, revisionId?: number): boolean {
    return selectedTracks.has(getTrackKey(songId, revisionId));
  }

  async function handleAddTracks() {
    if (!playlistId || selectedTracks.size === 0) return;

    const tracks: TrackSelection[] = [];

    // Build tracks array from selections
    selectedTracks.forEach(key => {
      const [songIdStr, revisionIdStr] = key.split('-');
      const songId = Number(songIdStr);
      const revisionId = revisionIdStr ? Number(revisionIdStr) : undefined;

      const song = songs.find(s => s.songId === songId);
      if (!song) return;

      if (revisionId) {
        const details = songDetails.get(songId);
        const revision = details?.revisions.find(r => r.songRevisionId === revisionId);
        if (revision) {
          tracks.push({
            songId,
            songName: song.songName,
            coverArtUrl: revision.coverArtUrl || song.coverArtUrl,
            revisionId,
            revisionName: revision.songName,
          });
        }
      } else {
        tracks.push({
          songId,
          songName: song.songName,
          coverArtUrl: song.coverArtUrl,
        });
      }
    });

    await addTracksToPlaylist(playlistId, tracks);
    navigate(`/playlists/${playlistId}`);
  }

  if (isLoading) return <LoadingSpinner />;

  // Sort songs alphabetically
  const sortedSongs = [...songs].sort((a, b) => a.songName.localeCompare(b.songName));

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              to={`/playlists/${playlistId}`}
              className="flex items-center gap-2 text-text-secondary hover:text-white"
            >
              <ArrowLeft size={20} />
              Back to Playlist
            </Link>
            <button
              onClick={handleAddTracks}
              disabled={selectedTracks.size === 0}
              className="btn-primary disabled:opacity-50"
            >
              Add {selectedTracks.size} Track{selectedTracks.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-6">Select Tracks</h1>

        <div className="space-y-4">
          {sortedSongs.map((song) => {
            const details = songDetails.get(song.songId);
            const hasRevisions = details && details.revisions.length > 0;
            const isMainSongSelected = isTrackSelected(song.songId);

            return (
              <div key={song.songId} className="card">
                {/* Main Song */}
                <div
                  onClick={() => toggleTrack(song.songId, song.songName, song.coverArtUrl)}
                  className="flex items-center gap-4 cursor-pointer hover:bg-surface-hover transition-colors p-2 -m-2 rounded"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isMainSongSelected
                      ? 'bg-primary border-primary'
                      : 'border-text-muted'
                  }`}>
                    {isMainSongSelected && <Check size={14} className="text-black" />}
                  </div>

                  <CoverArt
                    songId={song.songId}
                    coverArtUrl={song.coverArtUrl}
                    alt={song.songName}
                    className="w-12 h-12 object-cover rounded"
                  />

                  <div className="flex-1">
                    <p className="text-white font-medium">{song.songName}</p>
                    {hasRevisions && (
                      <p className="text-text-muted text-sm">
                        {details.revisions.length} revision{details.revisions.length !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>

                {/* Revisions */}
                {hasRevisions && details && (
                  <div className="mt-3 ml-9 space-y-2 border-l-2 border-border pl-4">
                    {details.revisions.map((revision) => {
                      const isRevisionSelected = isTrackSelected(song.songId, revision.songRevisionId);

                      return (
                        <div
                          key={revision.songRevisionId}
                          onClick={() => toggleTrack(
                            song.songId,
                            song.songName,
                            revision.coverArtUrl || song.coverArtUrl,
                            revision.songRevisionId,
                            revision.songName
                          )}
                          className="flex items-center gap-4 cursor-pointer hover:bg-surface-hover transition-colors p-2 -m-2 rounded"
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isRevisionSelected
                              ? 'bg-primary border-primary'
                              : 'border-text-muted'
                          }`}>
                            {isRevisionSelected && <Check size={14} className="text-black" />}
                          </div>

                          <CoverArt
                            songId={song.songId}
                            revisionId={revision.songRevisionId}
                            coverArtUrl={revision.coverArtUrl}
                            alt={revision.songName}
                            className="w-10 h-10 object-cover rounded"
                          />

                          <div className="flex-1">
                            <p className="text-text-secondary text-sm">{revision.songName}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
